import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '../../utils/roleUtils';
import { usePreferences } from '../../contexts/PreferenceContext';
import { useAdminBookingWebSocket } from '../../hooks/useAdminBookingWebSocket';
import ActiveDriversPanel from '../booking-management/ActiveDriversPanel';
import OperationalUnavailabilityWorkspace from '../operational-unavailability/OperationalUnavailabilityWorkspace';
import OperationsTabBar from './OperationsTabBar';
import AdminTripAssignmentWorkspace from './AdminTripAssignmentWorkspace';

const TAB_PREFIX = 'admin-operations';

const AdminOperationsWorkspace = ({ currentEntity, userRole }) => {
  const { t } = useTranslation();
  const { userPreferences } = usePreferences();
  const [activeTab, setActiveTab] = useState('onDutyDrivers');
  const [activeDriversRefreshKey, setActiveDriversRefreshKey] = useState(0);
  const entityId = currentEntity?.entityId;
  const adminMode = isAdmin(userRole);
  const tabs = [
    { id: 'onDutyDrivers', label: t('operations.onDutyDrivers') },
    { id: 'tripAssignments', label: t('operations.tripAssignments') },
    { id: 'serviceBlocks', label: t('operations.serviceBlocks') }
  ];
  const preferredDateFormat =
    userPreferences?.date_format
    || userPreferences?.dateFormat
    || 'MM/DD/YYYY';
  const preferredTimeFormat =
    userPreferences?.time_format
    || userPreferences?.timeFormat
    || '12h';

  const handleAdminEvent = useCallback((event) => {
    if (event.type === 'driver_availability_changed') {
      setActiveDriversRefreshKey((value) => value + 1);
    }
  }, []);

  useAdminBookingWebSocket(entityId, adminMode && Boolean(entityId), handleAdminEvent);

  return (
    <div className="mx-auto max-w-7xl px-4 pb-8 pt-6 sm:px-6 lg:px-8">
      <OperationsTabBar
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        idPrefix={TAB_PREFIX}
      />

      <section
        id={`${TAB_PREFIX}-panel-onDutyDrivers`}
        role="tabpanel"
        aria-labelledby={`${TAB_PREFIX}-tab-onDutyDrivers`}
        hidden={activeTab !== 'onDutyDrivers'}
        className="pt-6"
      >
        {activeTab === 'onDutyDrivers' && (
          <ActiveDriversPanel
            entityId={entityId}
            refreshKey={activeDriversRefreshKey}
            preferredDateFormat={preferredDateFormat}
            preferredTimeFormat={preferredTimeFormat}
          />
        )}
      </section>

      <section
        id={`${TAB_PREFIX}-panel-tripAssignments`}
        role="tabpanel"
        aria-labelledby={`${TAB_PREFIX}-tab-tripAssignments`}
        hidden={activeTab !== 'tripAssignments'}
        className="pt-6"
      >
        {activeTab === 'tripAssignments' && (
          <AdminTripAssignmentWorkspace currentEntity={currentEntity} />
        )}
      </section>

      <section
        id={`${TAB_PREFIX}-panel-serviceBlocks`}
        role="tabpanel"
        aria-labelledby={`${TAB_PREFIX}-tab-serviceBlocks`}
        hidden={activeTab !== 'serviceBlocks'}
        className="pt-6"
      >
        {activeTab === 'serviceBlocks' && (
          <OperationalUnavailabilityWorkspace
            entityId={entityId}
            adminMode
          />
        )}
      </section>
    </div>
  );
};

export default AdminOperationsWorkspace;
